import { Box, Button, Paragraph } from '@/src/packages/components';
import { useProductPrice } from '@/src/packages/hooks/use-product-price';
import { useBasket } from '@/src/packages/components/contexts/basket.context';
import {
  ANALYTICS_EVENT_CATEGORIES,
  ANALYTICS_EVENT_NAMES,
  trackEvent,
} from '@/src/packages/server/clients/analytics';
import {
  CreateNotificationSchema,
  ICreateNotificationSchema,
  IFetchProductPriceListByCountry,
  IFetchProductVariantSchema,
} from '@bitmap/contracts';
import { Input } from '@/src/packages/components/ui/Input';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/src/packages/components/ui/Form/HookForm';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { client } from '@/src/packages/server/clients/api';
import { Strong } from '@/src/packages/components/ui/Strong';
import { useEffect } from 'react';
import { Panel } from '@/src/packages/components/ui/Panel';

const ProductOutOfStockForm = ({ skuId }: { skuId: number | null }) => {
  const form = useForm<ICreateNotificationSchema>({
    resolver: zodResolver(CreateNotificationSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const { mutate, isLoading, isSuccess } =
    client.notififications.create.useMutation({
      onError: () => {
        alert('Something went wrong, please try again.');
      },
    });

  useEffect(() => {
    if (skuId) {
      form.reset({ skuId });
    }
  }, [skuId]);

  const { control } = form;

  const handleSubmit = (values: ICreateNotificationSchema) => {
    mutate({ body: values });
  };

  if (isSuccess) {
    return (
      <Panel className="p-5">
        Thank you, we will let you know when this item comes back in stock.
      </Panel>
    );
  }

  return (
    <Box className="space-y-2">
      <Paragraph>
        <Strong className="text-red-500">Out of stock.</Strong> Sign up to
        receive an email when it comes back in.
      </Paragraph>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Box className="space-y-4">
            <FormField
              control={control}
              name="email"
              render={({ field, fieldState: { isDirty, error } }) => (
                <FormItem>
                  <FormControl>
                    <>
                      <Input
                        type="text"
                        disabled={isLoading}
                        placeholder="Enter your email address"
                        {...field}
                      />

                      {isDirty && <FormMessage />}
                    </>
                  </FormControl>
                </FormItem>
              )}
            />

            <Button
              isBlock
              variant="solid"
              color="black"
              type="submit"
              isLoading={isLoading}
              isDisabled={!form.formState.isValid}
            >
              Notify me
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

export const ProductAddToCartButton = ({
  selectedSku,
  priceList,
}: {
  selectedSku: IFetchProductVariantSchema['skus'][0] | null;
  priceList: IFetchProductPriceListByCountry;
}) => {
  const { addItem, isUpdating } = useBasket();

  const prices = useProductPrice(priceList);

  const handleClick = () => {
    if (selectedSku) {
      addItem([{ skuId: selectedSku.id, quantity: 1 }]);

      trackEvent({
        name: ANALYTICS_EVENT_NAMES.PRODUCT_PAGE_ADD_TO_BAG,
        category: ANALYTICS_EVENT_CATEGORIES.PRODUCT_PAGE,
      });
    }
  };

  if (selectedSku && selectedSku?.stock === 0) {
    return <ProductOutOfStockForm skuId={selectedSku?.id} />;
  }

  return (
    <Button
      isBlock
      variant="solid"
      color="black"
      size="lg"
      isLoading={isUpdating}
      isDisabled={!selectedSku}
      onClick={handleClick}
    >
      {`Add to bag – ${prices?.priceGrossFormatted}`}
    </Button>
  );
};
